// Menu utility functions — clone từ attendance-app's adminMenu.js (line 532-600).
// Khác Core: nhận `menu` qua param thay vì closure trên `adminMenu` global → consumer-agnostic.
//
// Schema của menu:
//   [{ key, label, icon, basePath, path?, items?: [{ key, label, path, group?, end?, permissions? }] }]
//
// Single-link group: có `path` (hoặc `basePath`) + KHÔNG có `items` → click navigate trực tiếp,
// không show secondary submenu.

const PATH_ALIASES = {};

export function findActiveGroup(menu, pathname) {
  if (!pathname?.startsWith('/')) return menu[0] || null;

  // Single-link group match (group có path nhưng không có items)
  const singleLink = menu.find((g) => g.path && !g.items && (pathname === g.path || pathname.startsWith(`${g.path}/`)));
  if (singleLink) return singleLink;

  // Exact item match first (longer paths win)
  const allItems = menu.flatMap((g) => (g.items || []).map((item) => ({ group: g, item })));
  allItems.sort((a, b) => b.item.path.length - a.item.path.length);
  const exactMatch = allItems.find(({ item }) => pathname === item.path || pathname.startsWith(`${item.path}/`));
  if (exactMatch) return exactMatch.group;

  // Base path match
  const baseGroup = menu.find((group) => group.basePath && pathname.startsWith(group.basePath));
  if (baseGroup) return baseGroup;

  // Fallback to first group
  return menu[0] || null;
}

export function findActiveItem(menu, pathname, search = '') {
  const group = findActiveGroup(menu, pathname);
  if (!group?.items?.length) return null;

  let resolved = pathname;
  for (const [alias, target] of Object.entries(PATH_ALIASES)) {
    if (pathname.startsWith(alias)) { resolved = target; break; }
  }

  // Sort longest first → query-bearing items checked trước plain item
  const sortedItems = [...group.items].sort((a, b) => b.path.length - a.path.length);
  const urlParams = new URLSearchParams(search || (typeof window !== 'undefined' ? window.location.search : ''));

  return sortedItems.find((item) => {
    const [itemPath, itemQuery] = (item.path || '').split('?');
    const pathMatch = resolved === itemPath || (itemPath !== group.basePath && resolved.startsWith(`${itemPath}/`));
    if (!pathMatch) return false;
    if (!itemQuery) {
      const siblings = group.items.filter((s) => s !== item && (s.path || '').includes('?'));
      for (const sib of siblings) {
        const [sibPath, sibQuery] = sib.path.split('?');
        if (sibPath !== itemPath) continue;
        const sibParams = new URLSearchParams(sibQuery);
        const allMatch = [...sibParams].every(([k, v]) => urlParams.get(k) === v);
        if (allMatch) return false;
      }
      return true;
    }
    const itemParams = new URLSearchParams(itemQuery);
    return [...itemParams].every(([k, v]) => urlParams.get(k) === v);
  }) || group.items[0] || null;
}

export function getBreadcrumbs(menu, pathname) {
  const group = findActiveGroup(menu, pathname);
  const item = findActiveItem(menu, pathname);
  const crumbs = [];
  // Nhóm single-link (không `items`) thì nhánh dưới bỏ trắng ⇒ thanh trên cùng v2 hiện
  // breadcrumb RỖNG. Với nhóm loại này tên nhóm CHÍNH LÀ tên trang → dùng luôn.
  // Giữ khớp với bản Core (attendance-app/admin/src/navigation/adminMenu.js) — hai bản là
  // clone có chủ đích, lệch một nhánh là hai host hiện breadcrumb khác nhau.
  if (group?.path && !group.items?.length) crumbs.push({ label: group.label, path: group.path });
  else if (group?.items?.length) crumbs.push({ label: group.label, path: group.items[0]?.path || group.basePath });
  if (item && item.label !== group?.label) crumbs.push({ label: item.label, path: item.path });
  return crumbs;
}

// Filter menu by permissions. Default: pass-through (no permissions check).
// Consumer pass `permissions: ['*']` hoặc undefined để skip filter.
// Per-item permissions filter: nếu item.permissions là array, item chỉ visible khi user có ít nhất 1 perm match.
export function filterMenuByPermissions(menu, permissions) {
  if (!permissions || permissions.includes('*')) return menu;
  const permSet = new Set(permissions);
  return menu
    .map((group) => {
      const visibleItems = (group.items || []).filter((item) => {
        if (!item.permissions || item.permissions.length === 0) return true;
        return item.permissions.some((p) => permSet.has(p));
      });
      // Group có items: chỉ giữ nếu còn ít nhất 1 item visible
      if (group.items?.length && visibleItems.length === 0) return null;
      return { ...group, items: visibleItems.length ? visibleItems : group.items };
    })
    .filter(Boolean);
}
